/**
 * E-GTCC-VIEW-TEN1, tab "Mitglieder".
 *
 * Reference: `GTCC_REFERENCE_2026-09-01.html:726-733` — the panel "Mitglieder
 * (GELETEC)" with the head addition "[+ Einladen] [+ Direkt anlegen]" and one
 * `.row` per member. "Einladungen" is its own reference tab
 * (`InvitationsTab.tsx`), not a second column here — B-GTCC-FIX-TEN1 undoes
 * the combined layout E-GTCC-VIEW-TEN1 shipped as a deliberate Owner-order
 * deviation (`ABWEICHUNGEN.md` §E "ten", point 1).
 *
 * The rows are the reference's; every control behind them is the gtcc1 card,
 * reused unchanged: the role dropdown, suspend and remove live in
 * `MembersSection` (`GtccTenantsView.tsx`). "Direkt anlegen" is not a
 * separate endpoint — gtcc1 creates a member by inviting them, so the head
 * addition names the one path that exists and the row detail is that path.
 */
import { EmptyState, Stack } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { MembersSection } from "../GtccTenantsView.js";
import { fetchTenMembers, fetchTenTenant } from "../../../api/ten.js";
import {
  TEN_QUERY_OPTIONS,
  TenGate,
  TenPanel,
  TenRow,
  memberValueText,
  type TenTabProps,
} from "../components/TenPanels.js";
import type { FleetTone } from "../../cockpit/components/FleetPrimitives.js";

/** Ein aktives Mitglied ist gruen, ein gesperrtes eine Warnung, alles Unbekannte grau. */
function memberTone(status: string | null | undefined): FleetTone {
  if (status === "active") return "ok";
  if (status === "suspended" || status === "pending") return "warn";
  return "muted";
}

export function MembersTab(props: TenTabProps): ReactNode {
  const { t } = useLocale();
  const tenantId = props.selectedTenantId;
  const enabled = tenantId !== null;
  const id = tenantId ?? "";

  const detail = useQuery({
    queryKey: ["gtcc", "ten", "view", "tenant", id],
    queryFn: ({ signal }) => fetchTenTenant(id, signal),
    enabled,
    ...TEN_QUERY_OPTIONS,
  });
  const members = useQuery({
    queryKey: ["gtcc", "ten", "view", "members", id],
    queryFn: ({ signal }) => fetchTenMembers(id, signal),
    enabled,
    ...TEN_QUERY_OPTIONS,
  });

  if (tenantId === null) {
    return (
      <div data-testid="gtcc-ten-members-no-tenant">
        <EmptyState
          title={t("admin.gtcc.ten.t360.no_tenant_title")}
          body={t("admin.gtcc.ten.t360.no_tenant_body")}
        />
      </div>
    );
  }

  const tenantName =
    detail.data !== undefined && detail.data.ok && detail.data.data
      ? (detail.data.data.displayName ?? tenantId)
      : tenantId;

  return (
    <TenPanel
      testId="gtcc-ten-members"
      title={t("admin.gtcc.ten.members_tab.panel", { name: tenantName })}
      note={t("admin.gtcc.ten.members_tab.panel_note")}
    >
      <Stack gap={2}>
        <TenGate
          query={members}
          testId="gtcc-ten-members-gate"
          render={(rows) =>
            rows.length === 0 ? (
              <div data-testid="gtcc-ten-members-empty">
                <EmptyState title={t("admin.gtcc.ten.members.empty")} />
              </div>
            ) : (
              <Stack gap={0}>
                {rows.map((row) => (
                  <TenRow
                    key={row.id}
                    testId={`gtcc-ten-member-${row.id}`}
                    tone={memberTone(row.status)}
                    label={row.displayName ?? row.email ?? row.id}
                    value={memberValueText(t, row.role, row.status)}
                  />
                ))}
              </Stack>
            )
          }
        />
        {/*
         * Die Schreibflaeche der alten Konsole, unveraendert darunter: sie
         * bringt Rollenwechsel, Sperren und Entfernen mit — hier wird davon
         * nichts nachgebaut.
         */}
        <MembersSection tenantId={tenantId} />
      </Stack>
    </TenPanel>
  );
}
